import { Shield, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";

const PrivacyPolicy = () => {
  useSEO({
    title: "Privacy Policy | Global Conflict Monitor",
    description: "Read how Global Conflict Monitor collects, uses, and protects your data. Learn about cookies, analytics, payment processing, and your privacy rights.",
    path: "/privacy",
    ogType: "article",
  });
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto max-w-3xl px-4 py-12">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <div className="mb-8 flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Privacy Policy</h1>
        </div>

        <p className="mb-6 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

        <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">1. Introduction</h2>
            <p>Global Conflict Monitor ("we", "us", "our") operates the globalconflictsmonitor.com website. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">2. Information We Collect</h2>
            <p className="mb-2">We may collect information about you in a variety of ways, including:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li><strong>Personal Data:</strong> Email address and account credentials when you register.</li>
              <li><strong>Payment Data:</strong> Payment information is processed securely via Stripe. We do not store your full card details.</li>
              <li><strong>Usage Data:</strong> Browser type, access times, pages viewed, and referring URL.</li>
              <li><strong>Cookies:</strong> We use essential cookies to maintain your session and preferences.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">3. How We Use Your Information</h2>
            <ul className="ml-4 list-disc space-y-1">
              <li>To provide and maintain the platform and your account.</li>
              <li>To process transactions and send related information.</li>
              <li>To send you updates, alerts, and administrative messages.</li>
              <li>To improve our platform and develop new features.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">4. Data Sharing</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share data with trusted service providers (e.g., Stripe for payments) who assist us in operating our platform, subject to strict confidentiality obligations.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">6. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to access, correct, or delete your personal data. To exercise these rights, contact us at <a href="mailto:support@globalconflictsmonitor.com" className="text-primary hover:underline">support@globalconflictsmonitor.com</a>.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">7. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p className="mt-2"><a href="mailto:support@globalconflictsmonitor.com" className="text-primary hover:underline">support@globalconflictsmonitor.com</a></p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
